import React, { useRef, useState } from 'react';

import compact from 'lodash/compact';
import sumBy from 'lodash/sumBy';
import { useSnackbar } from 'notistack';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
} from '@mui/material';

import { useGetOffer } from '~/hooks/apis/offers/useGetOffer';
import { useGetOpportunityRelated } from '~/hooks/apis/offers/useGetOpportunityRelated';
import { useSubmitPricingTool } from '~/hooks/apis/offers/usePricingToolSubmissions';

import { formatDateShortWithClock } from '~/services/TimeService';

import ApiError from '~/types/ApiError';

import { usePricingToolSession } from '../PricingToolSessionContext';
import { PRICING_OPTION_LABELS } from '../constants';
import type { CommercialApprovalData } from '../export/types';
import { DEFAULT_OPTION_STATE } from '../tabs/packageOptions/packageOptionsModel';

import SubmissionSection from './SubmissionSection';
import {
  type SalesforceSubmission,
  buildSalesforceSubmission,
  excludeRecordsWithLongNames,
  unsyncedAssetLinkNames,
} from './buildSalesforceSubmission';
import { findMissingRequiredItems } from './findMissingRequiredItems';
import { type RecordKind, diffSubmission } from './submissionDiff';
import { useRequiredItemsInput } from './useRequiredItemsInput';

const RECORD_LABELS: Array<[RecordKind, string]> = [
  ['deal_offers', 'Deal options'],
  ['room_assets', 'New room assets'],
  ['inclusion_assets', 'New inclusion assets'],
  ['inclusions', 'Inclusions'],
  ['bundled_experiences', 'Bundled experiences'],
  ['blackouts', 'Blackouts'],
  ['surcharge_periods', 'Surcharge periods'],
  ['extra_guest_surcharges', 'Extra guest surcharges'],
  ['override_tiers', 'Override tiers'],
];

const SALESFORCE_GRAPH_LIMIT = 500;

type Props = {
  offerId?: string;
  forecast: CommercialApprovalData['forecastSummary'] | null;
};

type SubmitError = { title: string; messages: Array<string> };

type SubmitNotice = { blocksSubmit: boolean; title: string; detail?: string; items: Array<string> };

function salesforceErrors(error: unknown) {
  return (
    (error as ApiError)?.responseBody as {
      result?: {
        salesforce_result?: { errors?: Array<{ message: string; reference_id?: string; error_code?: string }> };
      };
    }
  )?.result?.salesforce_result?.errors;
}

// Mirrors svc-offer's rejection check.
function isSalesforceRejection(error: unknown) {
  return !!salesforceErrors(error)?.some(({ reference_id, error_code }) => reference_id || error_code);
}

function isRejected(error: unknown) {
  return (
    error instanceof ApiError && (isSalesforceRejection(error) || (error.status !== undefined && error.status < 500))
  );
}

// A same-key replay would return the failed row, so any error list needs a new key.
function needsNewKey(error: unknown) {
  return isRejected(error) || !!salesforceErrors(error)?.length;
}

function describeSubmitError(error: unknown): SubmitError {
  if (error instanceof ApiError && error.status === 409) {
    return {
      title: 'Another submission for this opportunity is still being processed. Wait a minute, then submit again.',
      messages: [],
    };
  }
  const errors = salesforceErrors(error);
  const isGatewayTimeout = error instanceof ApiError && error.status === 504;
  const messages = errors?.length
    ? errors.map(({ message, reference_id }) => (reference_id ? `${reference_id}: ${message}` : message))
    : isGatewayTimeout
      ? []
      : [(error as Error).message];
  const reached = isRejected(error)
    ? 'The submission to Salesforce failed.'
    : 'The submission may not have reached Salesforce.';
  return { title: `${reached} Submitting again is safe; it will not duplicate records.`, messages };
}

export default function SubmitToSalesforceButton({ offerId, forecast }: Props) {
  const session = usePricingToolSession();
  const {
    preferredOption,
    saveDraft,
    isSavingDraft,
    lastConfirmedSubmission: lastConfirmed,
    submissionHistoryStatus,
  } = session;
  const { enqueueSnackbar } = useSnackbar();
  const { data: offer } = useGetOffer(offerId);
  const submitMutation = useSubmitPricingTool(offerId, offer?.vendor_account_id);
  const { data: opportunity, isLoading: isLoadingOpportunity } = useGetOpportunityRelated(offerId);
  // Outlives the dialog so a retry after an unknown outcome reuses the key.
  const unresolvedAttempt = useRef<{ idempotencyKey: string; payload: string } | null>(null);

  const [pending, setPending] = useState<{
    idempotencyKey: string;
    submission: SalesforceSubmission;
    excludedReasons: Array<string>;
    excludedFromLastSubmission: Array<string>;
    changes: ReturnType<typeof diffSubmission>;
  } | null>(null);
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);

  const isBusy = isSavingDraft || submitMutation.isPending;
  const unsyncedAssetLinks = pending ? unsyncedAssetLinkNames(pending.submission) : [];
  const missingRequiredItems = findMissingRequiredItems(useRequiredItemsInput());
  const notices: Array<SubmitNotice> = compact([
    missingRequiredItems.length > 0 && {
      blocksSubmit: true,
      title: 'Fill in these required items before submitting',
      items: missingRequiredItems,
    },
    unsyncedAssetLinks.length > 0 && {
      blocksSubmit: true,
      title: 'Linked to an asset not yet synced from Salesforce',
      detail: 'Link a synced asset or create a new one.',
      items: unsyncedAssetLinks,
    },
    !!pending?.excludedFromLastSubmission.length && {
      blocksSubmit: true,
      title: 'Fix these names before submitting',
      detail: 'These were in the last submission. Submitting without them would delete them in Salesforce.',
      items: pending.excludedFromLastSubmission,
    },
    pending?.submission.deal_offers.length === 0 && {
      blocksSubmit: true,
      title: 'There are no deal options to submit',
      detail: 'Add a sell price to at least one room first.',
      items: [],
    },
    !!pending?.excludedReasons.length && {
      blocksSubmit: false,
      title: "These won't be submitted",
      detail: 'Their names are too long for Salesforce. Shorten them and submit again to add them.',
      items: pending.excludedReasons,
    },
  ]);
  const recordCount = pending ? sumBy(RECORD_LABELS, ([kind]) => pending.submission[kind].length) : 0;
  const isBlocked = notices.some(({ blocksSubmit }) => blocksSubmit);

  const handleOpen = () => {
    setSubmitError(null);
    const fullSubmission = buildSalesforceSubmission({
      preferredOption,
      propertySetup: session.propertySetup.values,
      optionState: session.packageOptions.optionStates[preferredOption] ?? DEFAULT_OPTION_STATE,
      roomAssets: session.packageOptions.roomAssets,
      inclusionDrafts: session.inclusions.drafts,
      inclusionAssets: session.inclusions.inclusionAssets,
      blackouts: session.blackouts.blackouts,
      surchargePeriods: session.surcharges.session.grids.periods,
      egs: session.egs.drafts,
      opportunity,
      previousOpportunity: lastConfirmed?.request_snapshot.opportunity,
      overrides: session.overrides.overrides.options[preferredOption],
      forecast,
    });
    const { submission, excludedReasons, excludedFromLastSubmission } = excludeRecordsWithLongNames(
      fullSubmission,
      lastConfirmed?.request_snapshot,
    );
    const payload = JSON.stringify(submission);
    const idempotencyKey =
      unresolvedAttempt.current?.payload === payload ? unresolvedAttempt.current.idempotencyKey : crypto.randomUUID();
    setPending({
      idempotencyKey,
      submission,
      excludedReasons,
      excludedFromLastSubmission,
      changes: diffSubmission(submission, lastConfirmed?.request_snapshot),
    });
  };

  const handleSubmit = async () => {
    if (!pending) {
      return;
    }
    setSubmitError(null);
    // Save first so its version check stops a stale tab; a same-key retry is already saved.
    const isRetry = unresolvedAttempt.current?.idempotencyKey === pending.idempotencyKey;
    try {
      if (!isRetry) {
        await saveDraft();
      }
    } catch (error) {
      setSubmitError({
        title: 'The session could not be saved, so nothing was sent to Salesforce.',
        messages: [
          error instanceof ApiError && error.status === 409
            ? 'This session was saved elsewhere since you opened it. Reload the page before submitting.'
            : (error as Error).message,
        ],
      });
      return;
    }
    unresolvedAttempt.current = { idempotencyKey: pending.idempotencyKey, payload: JSON.stringify(pending.submission) };
    try {
      const result = await submitMutation.mutateAsync({
        idempotency_key: pending.idempotencyKey,
        submission: pending.submission,
      });
      if (result.status === 'pending') {
        setSubmitError({
          title: 'This submission is still being processed. Wait a minute, then click Submit again to see the result.',
          messages: [],
        });
        return;
      }
      unresolvedAttempt.current = null;
      const unsaved = result.salesforce_result?.errors ?? [];
      if (unsaved.length > 0) {
        setSubmitError({
          title: 'Submitted to Salesforce, but some inclusions were not saved. Submit again to save them.',
          messages: unsaved.map(({ message }) => message),
        });
        // A same-key replay returns this submission instead of saving the rest.
        setPending((current) => current && { ...current, idempotencyKey: crypto.randomUUID() });
        return;
      }
      setPending(null);
      enqueueSnackbar('Submitted to Salesforce', { variant: 'success' });
    } catch (error) {
      setSubmitError(describeSubmitError(error));
      if (needsNewKey(error)) {
        unresolvedAttempt.current = null;
        setPending((current) => current && { ...current, idempotencyKey: crypto.randomUUID() });
      }
    }
  };

  const disabledReason =
    submissionHistoryStatus === 'error'
      ? 'Previous submissions could not be loaded. Reload the page before submitting.'
      : null;
  const button = (
    <Button
      disabled={!!disabledReason || submissionHistoryStatus === 'pending' || isBusy || isLoadingOpportunity}
      onClick={handleOpen}
      size="small"
      variant="contained"
    >
      {lastConfirmed ? 'Resubmit to Salesforce' : 'Submit to Salesforce'}
    </Button>
  );

  return (
    <>
      {disabledReason ? (
        <Tooltip title={disabledReason}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}

      <Dialog fullWidth maxWidth="md" onClose={() => !isBusy && setPending(null)} open={pending !== null}>
        <DialogTitle>Submit {PRICING_OPTION_LABELS[preferredOption]} to Salesforce?</DialogTitle>
        <DialogContent>
          <DialogContentText gutterBottom>
            This session will be saved and the records below will be submitted to Salesforce. The submission is
            processed as a single transaction: if any record fails, no changes are made in Salesforce.
            {recordCount > SALESFORCE_GRAPH_LIMIT &&
              ` This submission has over ${SALESFORCE_GRAPH_LIMIT} records, so some inclusions are saved afterwards in separate batches.`}
          </DialogContentText>
          {notices.map(({ blocksSubmit, title, detail, items }) => (
            <Alert key={title} severity={blocksSubmit ? 'error' : 'warning'} sx={{ mb: 2 }}>
              <AlertTitle>{title}</AlertTitle>
              {detail}
              {items.length > 0 && (
                <Box component="ul" sx={{ mb: 0, mt: detail ? 1 : 0, pl: 2 }}>
                  {items.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </Box>
              )}
            </Alert>
          ))}
          {lastConfirmed && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>This updates the last submission</AlertTitle>
              Records from the submission on {formatDateShortWithClock(lastConfirmed.created_at)} by{' '}
              {lastConfirmed.created_by} will be updated, and any you've removed here will be deleted. Records added
              directly in Salesforce aren't affected.
            </Alert>
          )}
          {pending && (
            <>
              <SubmissionSection changes={pending.changes.opportunity} label="Opportunity fields" />
              {RECORD_LABELS.map(([kind, label]) => (
                <SubmissionSection changes={pending.changes.records[kind]} key={kind} label={label} />
              ))}
            </>
          )}
          {submitError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {submitError.title}
              <ul>
                {submitError.messages.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={isBusy} onClick={() => setPending(null)}>
            Cancel
          </Button>
          <Button disabled={isBusy || isBlocked} onClick={handleSubmit} variant="contained">
            {isSavingDraft ? 'Saving...' : submitMutation.isPending ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
